/**
 * Where kitchen pendants may hang: the centreline of a counter people SIT at,
 * as a line segment in world XZ, plus how many fixtures that segment carries.
 *
 * Pure — no I/O, no THREE. Consumed by `kitchenLighting.planPendants`.
 *
 * ## Islands and peninsulas are not symmetric
 *
 * An ISLAND is one countertop slab carrying `category.island`. `buildIsland`
 * already folds any seating overhang into that slab's own `dimensions` and
 * shifts its `center` toward the seats, so the slab IS the eating surface and
 * its long axis is the anchor.
 *
 * A PENINSULA is a wall run, and at most ONE of its legs is ever seated:
 * `keepBestSeatedLeg` (`materializeCandidate`) reduces the per-leg seating map
 * to a single entry before anything is emitted. So "which leg has the seating"
 * needs no heuristic and no depth probe — it is the leg whose top says so.
 *
 * Since generator 1.99.0 that top is ONE slab, knee overhang included, and it
 * says so itself: `seating` (`seatedPeninsulaTop.js`). A range on the leg
 * cuts it into pieces with a bar behind the range, each carrying `seating`,
 * all on the same dining edge — so the pieces of one edge are read as one
 * eating surface, as the knee strip was: its whole seated span, centred
 * between the dining edge and the counter's front.
 *
 * ## A kitchen applied before 1.99.0 has the knee STRIP
 *
 * A `peninsula_seating_overhang_*` slab with no `seating`, 12" deep, OUTBOARD
 * of the back panel — a pendant centred on it hangs over the diner's lap, not
 * over the counter. It supplies the seated SPAN and the leg's frame; the
 * counter run on the same leg supplies the depth. The pendant line is the
 * centreline of the two together, the same line the one slab gives.
 *
 * ## An island is ONE counter, whatever it is cut into
 *
 * A drawn island with a bend is a slab per leg plus the pieces each bend adds
 * (`corner-piece`), all tagged free-standing. Read piece by piece, every piece
 * got a pendant of its own, a 1 mm sliver at a bend included (the user,
 * 2026-11-03: "there's too many of them"). So an island's pieces are grouped
 * by the run they belong to (`islandGroups`), split into straight LEGS
 * (`legsOf`), and the row hangs (the user's rule, 2026-11-03):
 *
 *   - over the SEATING — the legs the bar stools stand along, and when the
 *     stools run round a bend, round it, up to `PENDANT_MAX_COUNT_BENT`;
 *   - else over the SINK, centred on it, along its leg;
 *   - else along the LONGEST straight leg.
 *
 * Bar stools are the one record of seating every producer writes: the
 * generator's island and peninsula builders and a drawn run's seated side all
 * place them (`barstoolSeating.buildBarstoolObjects`).
 *
 * ## Handedness
 *
 * Every kitchen builder stores `rotation.y = atan2(normal.x, normal.z)`, which
 * puts local +X at `(cos ry, -sin ry)` and local +Z (the inward normal) at
 * `(sin ry, cos ry)` — see `buildIsland.js`'s `along` and `shared-lib/
 * geometry/objectFootprint.js`. A `(cos, sin)` long axis is the MIRROR of that
 * and agrees only at 0/90/180/270; at 30 degrees it is out by up to a metre.
 */

const { objectFootprint } = require('../../geometry/objectFootprint');
const { objectWallIds } = require('../../cabinet-runs/objectWallIds');
const {
  doPolygonsOverlap,
} = require('../../bathroom-analyzer/pipeline/geometry');

// Minimum clear air between two adjacent pendant shades. Below this a row
// reads as one cluster rather than as separate fixtures.
const PENDANT_MIN_CLEAR_GAP_M = 0.457; // 18"
// Minimum clear air between the outermost shade and the end of the span, so a
// pendant never overhangs the counter it lights.
const PENDANT_END_MARGIN_M = 0.229; // 9"
// Most fixtures one counter carries, however long it is. Beyond three the row
// reads as track lighting.
const PENDANT_MAX_COUNT = 3;
// …unless the row turns a bend with the seating: a long bent peninsula's
// diners sit round it, and the row follows them (the user, 2026-11-03: "up to
// 4 or 5 lights even").
const PENDANT_MAX_COUNT_BENT = 5;
// Two pieces whose centrelines lie this close, parallel and touching, are one
// straight leg (a slab a seam or a range cut in two).
const SAME_LINE_TOL_M = 0.02;
const TOUCH_TOL_M = 0.05;
// A stool belongs to the leg it sits at: within this of the leg's centreline
// (half a counter, the knee, the stool) and along the leg's span.
const STOOL_REACH_M = 1.2;
const STOOL_SPAN_PAD_M = 0.3;
// Seated legs whose nearest ends lie this close are one stretch round a bend.
const BEND_JOIN_M = 1.0;
// Where a kitchen pendant's shade bottom lands, above the finished floor. The
// trade rule is 30-36" of air between the shade and the counter it lights: low
// enough to light the work surface, high enough that someone standing sees
// across the island beneath it. 72" is 36" over a standard 36" counter and 30"
// over a 42" bar top, so one floor-relative number sits inside the rule for
// both. Shared by the Fixtures & Finishes planner and `style_kitchen`.
const KITCHEN_PENDANT_BOTTOM_AFF_INCHES = 72;

function typeOf(o) {
  return (o?.type || o?.objectType || '').toLowerCase();
}
function cat(o, k) {
  return Boolean(
    o?.category && typeof o.category === 'object' && o.category[k]
  );
}
function isCountertop(o) {
  return typeOf(o) === 'countertop' || cat(o, 'countertop');
}
function dimY(o) {
  return o?.dimensions?.y ?? o?.dimensions?.height ?? 0;
}
function topY(o) {
  return (o?.center?.y ?? 0) + dimY(o) / 2;
}

/**
 * Long-axis unit direction (world XZ) and its length, in the handedness every
 * kitchen builder stores rotations in.
 */
function longAxisOf(rotY, dims) {
  const along = { x: Math.cos(rotY), z: -Math.sin(rotY) };
  const across = { x: Math.sin(rotY), z: Math.cos(rotY) };
  const dx = dims?.x ?? 0;
  const dz = dims?.z ?? 0;
  if (dx >= dz) return { dir: along, length: dx, normal: across };
  return { dir: across, length: dz, normal: { x: -along.x, z: -along.z } };
}

/** Inward normal (local +Z) for a stored `rotation.y`. */
function inwardNormalOf(rotY) {
  return { x: Math.sin(rotY), z: Math.cos(rotY) };
}

/** True when two stored rotations describe the same line (mod 180 degrees). */
function isParallel(rotA, rotB, tolRad = 0.05) {
  const d = Math.abs(((rotA - rotB) % Math.PI) + Math.PI) % Math.PI;
  return d < tolRad || Math.PI - d < tolRad;
}

/**
 * Counter runs belonging to the same peninsula leg as `slab`.
 *
 * Which field links a cabinet to its wall depends on who last emitted it:
 * the generator's apply persists `cabinetWallRefs` and strips `parentWallIds`,
 * the run engine's re-solve persists `parentWallIds` and no refs — so
 * `objectWallIds` reads both. (Reading the refs alone found no cabinet on a
 * peninsula the engine had re-solved, and the pendant row asked for a seated
 * peninsula over one with three pendants already hanging.) A run countertop
 * names neither, only `parents` naming its lower cabinets. So the chain is
 * slab -> cabinet-wall id -> cabinets on that wall -> countertops parented to
 * one of them.
 *
 * A corner cabinet belongs to BOTH legs of an L, which would drag the adjoining
 * leg's countertop in with it; the parallel test drops those, since the two
 * legs of an L meet at a right angle.
 */
function counterRunsOnLegOf(slab, objects) {
  const wallIds = new Set(objectWallIds(slab));
  if (!wallIds.size) return [];
  const cabinetIds = new Set(
    objects
      .filter((o) => objectWallIds(o).some((id) => wallIds.has(id)))
      .map((o) => o.id)
  );
  const slabRot = slab.rotation?.y ?? 0;
  return objects.filter(
    (o) =>
      isCountertop(o) &&
      !cat(o, 'seatingOverhang') &&
      isParallel(o.rotation?.y ?? 0, slabRot) &&
      (o.parents || []).some((p) => cabinetIds.has(p?.id))
  );
}

/**
 * The eating surface of a seated peninsula leg, as a pendant anchor.
 *
 * Depth is measured from the slab's OUTBOARD edge to the counter runs' INBOARD
 * edge, and the anchor sits at the middle of that. Returns null when the leg's
 * counter cannot be found — better no pendant than one hung over nothing.
 */
function peninsulaAnchorFor(slab, objects) {
  const runs = counterRunsOnLegOf(slab, objects);
  if (!runs.length) return null;

  const rotY = slab.rotation?.y ?? 0;
  const normal = inwardNormalOf(rotY);
  const slabCenter = slab.center || {};
  const alongNormal = (o) =>
    ((o.center?.x ?? 0) - (slabCenter.x ?? 0)) * normal.x +
    ((o.center?.z ?? 0) - (slabCenter.z ?? 0)) * normal.z;

  // Outboard face of the knee strip, and the far face of the deepest run.
  const outboardEdge = -(slab.dimensions?.z ?? 0) / 2;
  const inboardEdge = Math.max(
    ...runs.map((r) => alongNormal(r) + (r.dimensions?.z ?? 0) / 2)
  );
  if (!(inboardEdge > outboardEdge)) return null;
  const offset = (outboardEdge + inboardEdge) / 2;

  const { dir, length } = longAxisOf(rotY, slab.dimensions);
  return {
    sourceId: slab.id,
    kind: 'peninsula',
    center: {
      x: (slabCenter.x ?? 0) + normal.x * offset,
      z: (slabCenter.z ?? 0) + normal.z * offset,
    },
    dir,
    length,
    surfaceTopY: topY(slab),
  };
}

// Back edges of one dining edge, built from the same numbers, agree to well
// under a millimetre; a centimetre still parts two different counters.
const EDGE_TOL_M = 0.01;

/**
 * The seated slabs (`seating`) grouped by the dining edge they share: same
 * rotation, back edges on one line. A range splits a leg's top into pieces and
 * a bar, and a diner sees one counter.
 */
function seatedEdges(objects) {
  const edges = [];
  for (const o of objects) {
    if (!isCountertop(o) || !o.seating || !o.center || !o.dimensions) continue;
    const rotY = o.rotation?.y ?? 0;
    const normal = inwardNormalOf(rotY);
    const backAt =
      o.center.x * normal.x + o.center.z * normal.z - (o.dimensions.z ?? 0) / 2;
    const edge = edges.find(
      (e) =>
        Math.abs(Math.cos(e.rotY - rotY) - 1) < 1e-6 &&
        Math.abs(e.backAt - backAt) < EDGE_TOL_M
    );
    if (edge) edge.pieces.push(o);
    else edges.push({ rotY, normal, backAt, pieces: [o] });
  }
  return edges;
}

/**
 * A seated leg's eating surface, from its slabs: the whole seated span along
 * it, centred between the dining edge and the counter's front edge.
 */
function seatedEdgeAnchor(edge) {
  const { rotY, normal, backAt, pieces } = edge;
  const dir = { x: Math.cos(rotY), z: -Math.sin(rotY) };
  let lo = Infinity;
  let hi = -Infinity;
  let front = -Infinity;
  for (const p of pieces) {
    const along = p.center.x * dir.x + p.center.z * dir.z;
    const across = p.center.x * normal.x + p.center.z * normal.z;
    lo = Math.min(lo, along - p.dimensions.x / 2);
    hi = Math.max(hi, along + p.dimensions.x / 2);
    front = Math.max(front, across + p.dimensions.z / 2);
  }
  if (!(hi > lo) || !(front > backAt)) return null;
  const mid = (lo + hi) / 2;
  const off = (backAt + front) / 2;
  // The deepest piece names the anchor — it is the counter, a bar is not.
  const lead = [...pieces].sort(
    (a, b) => b.dimensions.z - a.dimensions.z || b.dimensions.x - a.dimensions.x
  )[0];
  return {
    sourceId: lead.id,
    kind: 'peninsula',
    center: {
      x: dir.x * mid + normal.x * off,
      z: dir.z * mid + normal.z * off,
    },
    dir,
    length: hi - lo,
    surfaceTopY: topY(lead),
  };
}

function seatedEdgeAnchors(objects) {
  return seatedEdges(objects).map(seatedEdgeAnchor).filter(Boolean);
}

/**
 * An island counter — a run standing clear of the walls — under EITHER of the
 * two vocabularies that describe one.
 *
 * `buildIsland` / `buildIslandWithSink` stamp `category.island`; the cabinet-run
 * engine (`shared-lib/cabinet-runs/freestanding.js`) stamps
 * `category.freestanding`. They mean the same thing, and which one a room
 * carries is not stable over its lifetime: any pass that re-solves the run —
 * `resolveRunIfApplianceResized`, which fires on every appliance swap, so on
 * every design-package apply — REPLACES `island_countertop_*`
 * (`countertop+island`) with `countertop_autorun_island_*`
 * (`countertop+freestanding`).
 *
 * Reading only `island` therefore worked until the user swapped an appliance,
 * and then reported "no island to go above" on a kitchen with an island plainly
 * in it. Matching both is a bridge over a real divergence, not a guard: the two
 * builders genuinely disagree about the word, and unifying them would have to
 * move every other reader (the recessed-can island skip, `mergeKitchenObjects`)
 * at the same time.
 */
function isIslandCounter(o) {
  return cat(o, 'island') || cat(o, 'freestanding');
}

const isCornerPiece = (o) =>
  typeOf(o) === 'corner-fill-countertop' || cat(o, 'corner-piece');
const isStool = (o) => typeOf(o) === 'bar-stool' || cat(o, 'bar-stool');
const isSink = (o) => {
  const t = typeOf(o);
  return t.includes('sink') && !t.includes('cabinet') && !t.includes('base');
};

/**
 * The island counters of a room, one group per island: the free-standing
 * pieces of one run together (a drawn or re-solved island), a piece with no
 * run on its own (a generated island is one slab). Bend pieces never carry a
 * row, so they are left out.
 */
function islandGroups(objects) {
  const groups = new Map();
  for (const o of objects) {
    if (!isCountertop(o) || o.seating || cat(o, 'seatingOverhang')) continue;
    if (!isIslandCounter(o) || isCornerPiece(o)) continue;
    if (!o.center || !o.dimensions) continue;
    const key = o.runId ? `run:${o.runId}` : `piece:${o.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(o);
  }
  return [...groups.values()];
}

/**
 * An island's straight legs: its pieces grouped by centreline — parallel,
 * on one line, touching. Each leg runs `lo..hi` along `d` at `across` along
 * `n`, both in world terms, so a point on it is `d * t + n * across`.
 */
function legsOf(pieces) {
  const legs = [];
  for (const p of pieces) {
    const { dir, length } = longAxisOf(p.rotation?.y ?? 0, p.dimensions);
    if (!(length > 0)) continue;
    const d =
      dir.x > 1e-9 || (Math.abs(dir.x) <= 1e-9 && dir.z > 0)
        ? dir
        : { x: -dir.x, z: -dir.z };
    const n = { x: -d.z, z: d.x };
    const at = p.center.x * d.x + p.center.z * d.z;
    const across = p.center.x * n.x + p.center.z * n.z;
    const lo = at - length / 2;
    const hi = at + length / 2;
    const leg = legs.find(
      (l) =>
        l.d.x * d.x + l.d.z * d.z > 1 - 1e-4 &&
        Math.abs(l.across - across) < SAME_LINE_TOL_M &&
        lo <= l.hi + TOUCH_TOL_M &&
        hi >= l.lo - TOUCH_TOL_M
    );
    if (leg) {
      leg.lo = Math.min(leg.lo, lo);
      leg.hi = Math.max(leg.hi, hi);
      leg.pieces.push(p);
      leg.topY = Math.max(leg.topY, topY(p));
    } else {
      legs.push({ d, n, across, lo, hi, pieces: [p], topY: topY(p) });
    }
  }
  return legs;
}

const legPoint = (leg, t) => ({
  x: leg.d.x * t + leg.n.x * leg.across,
  z: leg.d.z * t + leg.n.z * leg.across,
});
const legAt = (leg, p) => p.x * leg.d.x + p.z * leg.d.z;
const legOff = (leg, p) => p.x * leg.n.x + p.z * leg.n.z - leg.across;
const leadPiece = (leg) =>
  [...leg.pieces].sort((a, b) => b.dimensions.x - a.dimensions.x)[0];

/** Each leg's seated stretch `[a, b]` along it, from the stools at it. */
function seatedSpans(legs, stools) {
  const spans = new Map();
  for (const s of stools) {
    let best = null;
    for (const leg of legs) {
      const t = legAt(leg, s.center);
      const off = Math.abs(legOff(leg, s.center));
      if (off > STOOL_REACH_M) continue;
      if (t < leg.lo - STOOL_SPAN_PAD_M || t > leg.hi + STOOL_SPAN_PAD_M)
        continue;
      if (!best || off < best.off) best = { leg, t, off };
    }
    if (!best) continue;
    const half = Math.max(s.dimensions?.x ?? 0, s.dimensions?.z ?? 0) / 2;
    const span = spans.get(best.leg) || [Infinity, -Infinity];
    span[0] = Math.max(best.leg.lo, Math.min(span[0], best.t - half));
    span[1] = Math.min(best.leg.hi, Math.max(span[1], best.t + half));
    spans.set(best.leg, span);
  }
  return spans;
}

/** A straight anchor over `[a, b]` of one leg. */
function legAnchor(leg, a, b, runId, extra = {}) {
  return {
    sourceId: leadPiece(leg).id,
    kind: 'island',
    runId: runId ?? null,
    center: legPoint(leg, (a + b) / 2),
    dir: leg.d,
    length: b - a,
    surfaceTopY: leg.topY,
    ...extra,
  };
}

/**
 * The seated legs as stretches: legs whose seated spans meet at a bend are
 * one stretch, a polyline through their centrelines, each leg's span joined
 * to the next at the ends that face each other.
 */
function seatedStretches(spans) {
  const items = [...spans.entries()].map(([leg, [a, b]]) => ({ leg, a, b }));
  const ends = (it) => [legPoint(it.leg, it.a), legPoint(it.leg, it.b)];
  const gap = (p, q) => Math.hypot(p.x - q.x, p.z - q.z);
  const touching = (i, j) =>
    ends(i).some((p) => ends(j).some((q) => gap(p, q) <= BEND_JOIN_M));
  const left = new Set(items);
  const stretches = [];
  while (left.size) {
    // start from an item with at most one neighbour, so the chain is walked
    // from one end
    const pool = [...left];
    const start =
      pool.find(
        (i) => pool.filter((j) => j !== i && touching(i, j)).length <= 1
      ) || pool[0];
    const chain = [start];
    left.delete(start);
    for (;;) {
      const tail = chain[chain.length - 1];
      const next = [...left].find((j) => touching(tail, j));
      if (!next) break;
      chain.push(next);
      left.delete(next);
    }
    stretches.push(chain);
  }
  return stretches;
}

/** The polyline of a chain of seated legs, each span in walking order. */
function chainPath(chain) {
  const pts = [];
  let last = null;
  chain.forEach((it, k) => {
    let p0 = legPoint(it.leg, it.a);
    let p1 = legPoint(it.leg, it.b);
    const nextIt = chain[k + 1];
    const near = (p, q) => Math.hypot(p.x - q.x, p.z - q.z);
    if (last) {
      if (near(p1, last) < near(p0, last)) [p0, p1] = [p1, p0];
    } else if (nextIt) {
      const n0 = legPoint(nextIt.leg, nextIt.a);
      const n1 = legPoint(nextIt.leg, nextIt.b);
      const toNext = (p) => Math.min(near(p, n0), near(p, n1));
      if (toNext(p0) < toNext(p1)) [p0, p1] = [p1, p0];
    }
    pts.push(p0, p1);
    last = p1;
  });
  return pts;
}

function pathLength(path) {
  let L = 0;
  for (let i = 1; i < path.length; i++)
    L += Math.hypot(path[i].x - path[i - 1].x, path[i].z - path[i - 1].z);
  return L;
}

/** The point `s` along a polyline, and the direction it runs there. */
function pointOnPath(path, s) {
  let left = s;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    if (len < 1e-9) continue;
    const dir = { x: (b.x - a.x) / len, z: (b.z - a.z) / len };
    if (left <= len || i === path.length - 1) {
      const u = Math.max(0, Math.min(len, left));
      return { x: a.x + dir.x * u, z: a.z + dir.z * u, dir };
    }
    left -= len;
  }
  return { ...path[0], dir: { x: 1, z: 0 } };
}

/** An anchor that follows a polyline round a bend. */
function pathAnchor(chain, runId) {
  const path = chainPath(chain);
  const length = pathLength(path);
  const mid = pointOnPath(path, length / 2);
  const lead = [...chain].sort((i, j) => j.b - j.a - (i.b - i.a))[0];
  return {
    sourceId: leadPiece(lead.leg).id,
    kind: 'island',
    runId: runId ?? null,
    center: { x: mid.x, z: mid.z },
    dir: mid.dir,
    length,
    path,
    surfaceTopY: Math.max(...chain.map((it) => it.leg.topY)),
  };
}

/** The sink standing in one of `legs`, and the leg: by parent, else by place. */
function sinkOn(legs, objects) {
  const pieceLeg = new Map();
  for (const leg of legs) for (const p of leg.pieces) pieceLeg.set(p.id, leg);
  for (const o of objects) {
    if (!isSink(o) || !o.center) continue;
    const parentLeg = (o.parents || [])
      .map((p) => pieceLeg.get(p?.id))
      .find(Boolean);
    if (parentLeg) return { leg: parentLeg, sink: o };
    const leg = legs.find(
      (l) =>
        legAt(l, o.center) >= l.lo &&
        legAt(l, o.center) <= l.hi &&
        Math.abs(legOff(l, o.center)) <=
          Math.max(...l.pieces.map((p) => p.dimensions.z)) / 2
    );
    if (leg) return { leg, sink: o };
  }
  return null;
}

/** Where one island's pendants hang (see the module note). */
function islandAnchors(pieces, objects) {
  const legs = legsOf(pieces);
  if (!legs.length) return [];
  const runId = pieces[0].runId;
  const spans = seatedSpans(legs, objects.filter(isStool));
  if (spans.size) {
    return seatedStretches(spans).map((chain) =>
      chain.length === 1
        ? legAnchor(chain[0].leg, chain[0].a, chain[0].b, runId)
        : pathAnchor(chain, runId)
    );
  }
  const sink = sinkOn(legs, objects);
  if (sink) {
    const t = legAt(sink.leg, sink.sink.center);
    return [
      legAnchor(sink.leg, sink.leg.lo, sink.leg.hi, runId, {
        center: legPoint(sink.leg, t),
        bounds: { lo: sink.leg.lo - t, hi: sink.leg.hi - t },
      }),
    ];
  }
  const longest = [...legs].sort((a, b) => b.hi - b.lo - (a.hi - a.lo))[0];
  return [legAnchor(longest, longest.lo, longest.hi, runId)];
}

/**
 * Every counter in the room that should carry pendants.
 *
 * Islands qualify on their own, ONE anchor (or one per separate seated
 * stretch) per island however many pieces it is cut into; a peninsula
 * qualifies only through its seating, so an unseated peninsula — and the
 * range-width "back counter bridge", which is also a `countertop` tagged
 * `peninsula` — contributes nothing.
 */
function resolvePendantAnchors(objects = []) {
  const anchors = [...seatedEdgeAnchors(objects)];
  for (const o of objects) {
    if (!isCountertop(o) || o.seating) continue;
    if (cat(o, 'seatingOverhang')) {
      const a = peninsulaAnchorFor(o, objects);
      if (a) anchors.push(a);
    }
  }
  for (const group of islandGroups(objects)) {
    anchors.push(...islandAnchors(group, objects));
  }
  return anchors;
}

/**
 * How many pendants a span carries, from the span and the fixture's own width.
 *
 * Fixtures sit at a fixed pitch of one shade plus the minimum gap, centred on
 * the span, so a wider pendant yields fewer of them on the same counter.
 */
function pendantCountFor(spanM, pendantWidthM, max = PENDANT_MAX_COUNT) {
  if (!(spanM > 0) || !(pendantWidthM > 0)) return 0;
  const pitch = pendantWidthM + PENDANT_MIN_CLEAR_GAP_M;
  const usable = spanM - 2 * PENDANT_END_MARGIN_M + PENDANT_MIN_CLEAR_GAP_M;
  const fits = Math.floor(usable / pitch);
  return Math.max(1, Math.min(max, fits));
}

/** Evenly-pitched offsets along the span, centred on it. */
function pendantOffsets(count, pendantWidthM) {
  const pitch = pendantWidthM + PENDANT_MIN_CLEAR_GAP_M;
  const offsets = [];
  for (let i = 0; i < count; i++) offsets.push((i - (count - 1) / 2) * pitch);
  return offsets;
}

/**
 * Objects that occupy the vertical band a pendant hangs in, so nothing is hung
 * into a vent hood, an upper cabinet or a refrigerator.
 *
 * The test is GEOMETRIC and FLOOR-RELATIVE. It deliberately does not reuse
 * `shared-lib/lighting/obstructions.js`: that module's `HIGH_MOUNT_MIN_BOTTOM`
 * compares an object's bottom against an absolute world Y, which is only a
 * mount height in a building whose floor sits at y = 0 — on a storey below
 * zero it classifies nothing as high-mounted, and the island hood it is meant
 * to catch reads as clear.
 *
 * A vent hood's duct is render-only (`dimensions.chimneyHeight`), outside its
 * bounding box, so it is added back here — the duct is the part of an island
 * hood that shares a pendant's air.
 */
function pendantObstacles(objects, bandBottomY, bandTopY) {
  const out = [];
  for (const o of objects || []) {
    if (!o?.center || !o?.dimensions) continue;
    if (o.demoed === true) continue;
    const t = typeOf(o);
    if (t.includes('light') || t.includes('chandelier')) continue;
    const bottom = (o.center.y ?? 0) - dimY(o) / 2;
    const top = topY(o) + (o.dimensions.chimneyHeight || 0);
    if (top <= bandBottomY || bottom >= bandTopY) continue;
    const fp = objectFootprint(o);
    if (fp) out.push(fp);
  }
  return out;
}

/**
 * Pendant positions for one anchor, with any that collide with an obstacle
 * dropped rather than nudged — a pendant slid off the counter's centreline
 * reads as a mistake, where a gap in the row reads as deliberate.
 */
function planPendantPositions(anchor, { pendantWidthM, obstacles = [] } = {}) {
  const max = anchor.path ? PENDANT_MAX_COUNT_BENT : PENDANT_MAX_COUNT;
  const count = pendantCountFor(anchor.length, pendantWidthM, max);
  if (!count) return [];
  const half = pendantWidthM / 2;
  let offsets = pendantOffsets(count, pendantWidthM);
  // A row centred on a sink is slid, whole, back inside its leg.
  if (anchor.bounds) {
    const lo = anchor.bounds.lo + PENDANT_END_MARGIN_M + half;
    const hi = anchor.bounds.hi - PENDANT_END_MARGIN_M - half;
    const shift =
      Math.max(0, lo - offsets[0]) - Math.max(0, offsets[count - 1] - hi);
    offsets = offsets.map((o) => o + shift);
  }
  const at = anchor.path
    ? (off) => pointOnPath(anchor.path, anchor.length / 2 + off)
    : (off) => ({
        x: anchor.center.x + anchor.dir.x * off,
        z: anchor.center.z + anchor.dir.z * off,
      });
  const positions = [];
  for (const off of offsets) {
    const { x, z } = at(off);
    const p = { x, z };
    const fp = objectFootprint({
      center: { x: p.x, z: p.z },
      dimensions: { x: pendantWidthM, z: pendantWidthM },
      rotation: { y: 0 },
    });
    if (obstacles.some((o) => doPolygonsOverlap(fp, o))) continue;
    positions.push({ ...p, halfWidth: half });
  }
  return positions;
}

module.exports = {
  resolvePendantAnchors,
  isIslandCounter,
  planPendantPositions,
  pendantCountFor,
  pendantOffsets,
  pendantObstacles,
  longAxisOf,
  PENDANT_MIN_CLEAR_GAP_M,
  PENDANT_END_MARGIN_M,
  PENDANT_MAX_COUNT,
  PENDANT_MAX_COUNT_BENT,
  KITCHEN_PENDANT_BOTTOM_AFF_INCHES,
};
